/**
 * Layout utility for sensor vitals conditional rendering.
 * Derives layout case from shipment data (sensor count + vital count).
 */

import type { SingleShipmentDataExtended } from '@/apis/types';
import { sortSensorsByPriorityAndId } from '@/pages/Modals/utils/chartUtils';
import { getAvailableVitalsForSensor } from './sensorVitals';

export type SensorVitalsLayoutCase = 'inline' | 'single_many' | 'multi';

export interface SensorVitalsLayoutResult {
  layoutCase: SensorVitalsLayoutCase;
  sensorCount: number;
  vitalCount: number;
}

/**
 * Counts the number of unique sensors in the shipment_monitor array.
 * Returns 0 if no sensors, 1 if single sensor (no IDs or all same ID), 2+ if multiple unique IDs.
 * Handles edge case where single sensors don't have shipment_sensor_id field.
 */
function getSensorCount(
  shipment: Partial<SingleShipmentDataExtended> | null | undefined
): number {
  const summaryCount = shipment?.sensor_summary?.length ?? 0;
  if (summaryCount > 0) return summaryCount;
  if (shipment?.sensor) return 1;
  return 0;
}

/**
 * Returns layout case, sensor count, and vital count for conditional rendering of sensor vitals.
 * - inline: 0 sensors or 1 sensor with <5 vitals (vitals inline with "Shipment details" header)
 * - single_many: 1 sensor with >=5 vitals (full row above header)
 * - multi: 2+ sensors (each sensor in own row above header)
 */
export function getSensorVitalsLayout(
  shipment: Partial<SingleShipmentDataExtended> | null | undefined
): SensorVitalsLayoutResult {
  const sensorCount = getSensorCount(shipment);

  // For single-sensor case, compute vital count via getAvailableVitalsForSensor
  let vitalCount = 0;
  if (sensorCount === 1) {
    const availableVitals = getAvailableVitalsForSensor(null, shipment ?? {});
    vitalCount = availableVitals.length;
  }

  let layoutCase: SensorVitalsLayoutCase;

  if (sensorCount === 0) {
    layoutCase = 'inline';
  } else if (sensorCount >= 2) {
    layoutCase = 'multi';
  } else if (vitalCount >= 5) {
    layoutCase = 'single_many';
  } else {
    layoutCase = 'inline';
  }

  return {
    layoutCase,
    sensorCount,
    vitalCount,
  };
}

/** Derived flags for layout - use these instead of raw layoutCase checks. */
export function getLayoutFlags(layoutCase: SensorVitalsLayoutCase) {
  return {
    /** Show vitals in a full row above the "Shipment details" header */
    showVitalsAboveHeader:
      layoutCase === 'single_many' || layoutCase === 'multi',
    /** Show vitals inline in the header row next to "Shipment details" */
    showVitalsInline: layoutCase === 'inline',
    /** Show viability window in the header row (not as trailing on vitals row) */
    viabilityInHeader: layoutCase !== 'single_many',
  };
}

/** Strip " (Primary)" and trailing "-1"/"-2" when single sensor. */
const formatLabel = (label: string, single: boolean) =>
  !label || !single
    ? label
    : label
        .replace(/\s*\(Primary\)\s*/gi, '')
        .replace(/\s*-\d+\s*$/, '')
        .trim() || label;

/** Reformat an API YYYY-MM-DD date as MM-DD-YYYY. Avoids `new Date`, which parses a bare date as UTC midnight and renders the previous day west of UTC. */
const formatCalibrationDate = (value?: string | null) => {
  const [year, month, day] = (value ?? '').split('-');
  return year && month && day ? `${month}-${day}-${year}` : '--';
};

/** Comma-separated sensor labels, sensor_tracking_ids, and calibration dates for Product details. Uses sensor_summary + sensors or falls back to shipment.sensor. */
export function getProductDetailSensors(
  shipment: Partial<SingleShipmentDataExtended> | null | undefined
) {
  const fallbackDisplay = formatLabel(shipment?.sensor?.type ?? '--', true);
  const fallbackId = shipment?.sensor?.id ?? '--';
  if (!shipment?.sensor_summary?.length) {
    return {
      sensorDisplay: fallbackDisplay,
      sensorIdDisplay: fallbackId,
      calibrationDateDisplay: '',
    };
  }

  const byId = new Map<
    string,
    {
      label: string;
      priority?: number;
      sensorTrackingId?: string;
      calibrationLastDate?: string | null;
    }
  >();
  const sensors = Array.isArray(shipment?.sensors) ? shipment.sensors : [];
  for (const summary of shipment.sensor_summary) {
    const id = summary?.sensor_id?.trim();
    if (!id || byId.has(id)) continue;
    const sensor = sensors.find((s) => s?.id === id);
    byId.set(id, {
      label: summary?.sensor_name || sensor?.display_label || '',
      priority: summary?.priority ?? sensor?.priority,
      sensorTrackingId: sensor?.sensor_tracking_id,
      calibrationLastDate: sensor?.calibration_last_date,
    });
  }
  if (byId.size === 0)
    return {
      sensorDisplay: fallbackDisplay,
      sensorIdDisplay: fallbackId,
      calibrationDateDisplay: '',
    };

  const sorted = Array.from(byId.entries())
    .filter(([, v]) => v.label)
    .sort((a, b) =>
      sortSensorsByPriorityAndId(
        { priority: a[1].priority, id: a[0] },
        { priority: b[1].priority, id: b[0] }
      )
    );
  const single = sorted.length === 1;
  const ids = sorted.map(([, v]) => v.sensorTrackingId?.trim()).filter(Boolean);
  const hasCalibration = sorted.some(([, v]) => Boolean(v.calibrationLastDate));
  return {
    sensorDisplay: sorted
      .map(([, v]) => formatLabel(v.label, single))
      .join(', '),
    sensorIdDisplay: ids.length > 0 ? ids.join(', ') : fallbackId,
    calibrationDateDisplay: hasCalibration
      ? sorted
          .map(([, v]) => formatCalibrationDate(v.calibrationLastDate))
          .join(', ')
      : '',
  };
}

export interface SensorTableRow {
  id: string;
  name: string;
  productNames: string[];
  trackingId: string;
  calibrationDate: string;
}

/** Rows for the detail Sensors table: one per active sensor, named and ordered like the vitals rows. */
export function getSensorTableRows(
  shipment: Partial<SingleShipmentDataExtended> | null | undefined
): SensorTableRow[] {
  const sensors = Array.isArray(shipment?.sensors) ? shipment.sensors : [];
  const summary = new Map(
    (shipment?.sensor_summary ?? []).map((s) => [s.sensor_id, s])
  );
  const seen = new Set<string>();
  return sensors
    .flatMap((sensor) => {
      if (!sensor?.id || seen.has(sensor.id)) return [];
      seen.add(sensor.id);
      const entry = summary.get(sensor.id);
      return [
        {
          id: sensor.id,
          priority: entry?.priority ?? sensor.priority,
          row: {
            id: sensor.id,
            name: entry?.sensor_name || sensor.display_label || '--',
            productNames: (sensor.products ?? []).map((p) => p.name),
            trackingId: sensor.sensor_tracking_id?.trim() || '--',
            calibrationDate: formatCalibrationDate(
              sensor.calibration_last_date
            ),
          },
        },
      ];
    })
    .sort(sortSensorsByPriorityAndId)
    .map(({ row }) => row);
}
